import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUpIcon, BarChart3Icon, EyeIcon, MessageSquareIcon, HeartIcon, ArrowRightIcon, AlertCircleIcon, FlameIcon, Coins } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import SnapCard from '@/components/SnapCard';
import type { Snap } from '@/types/database.types';
import { getActivityThresholds, getSnapActivity } from '@/utils/snap';
import Button from '@/components/form/Button';

export default function TopSnaps() {
  const [topSnaps, setTopSnaps] = useState<Snap[]>([]);
  const [cashSnaps, setCashSnaps] = useState<Snap[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCash, setLoadingCash] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchTopSnaps = async () => {
      try {
        setLoading(true);
        
        // Get top 3 SNAPs based on a combination of votes, views, and likes
        const { data, error } = await supabase
          .from('snaps')
          .select(`
            id,
            title,
            total_votes,
            view_count,
            created_at,
            ends_at,
            is_system,
            cashsnap,
            creator_id,
            creator:user_profiles!inner(
              id,
              username,
              avatar_url,
              created_at,
              followers_count
            ),
            options:snap_options!snap_options_snap_id_fkey(*),
            comments:comments(count),
            categories:snap_categories(category),
            snap_likes:snap_likes(count)
          `)
          .eq('is_private', false) // Only public SNAPs
          .eq('cashsnap', false) // Exclude CashSnaps
          .order('total_votes', { ascending: false }) // Order by votes
          .limit(3);

        if (error) throw error;

        // Format the data
        const formattedSnaps = data.map(snap => ({
          ...snap,
          categories: snap.categories?.map((c: any) => c.category) || [],
          commentCount: snap.comments?.[0]?.count || 0,
          isOwner: user?.id === snap.creator_id,
          userLiked: false, // Default value, we don't track this for non-logged in users
          isMarked: false,
          likes_count: snap.snap_likes?.[0]?.count || 0 // Use the live count from snap_likes
        }));

        setTopSnaps(formattedSnaps);
      } catch (error) {
        console.error('Error fetching top SNAPs:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCashSnaps = async () => {
      try {
        setLoadingCash(true);
        
        // Get top CashSnaps
        const { data, error } = await supabase
          .from('snaps')
          .select(`
            id,
            title,
            total_votes,
            view_count,
            created_at,
            ends_at,
            is_system,
            cashsnap,
            creator_id,
            creator:user_profiles!inner(
              id,
              username,
              avatar_url,
              created_at,
              followers_count
            ),
            options:snap_options!snap_options_snap_id_fkey(*),
            comments:comments(count),
            categories:snap_categories(category),
            snap_likes:snap_likes(count)
          `)
          .eq('is_private', false) // Only public SNAPs
          .eq('cashsnap', true) // Only CashSnaps
          .order('created_at', { ascending: false }) // Order by creation date
          .limit(3);

        if (error) throw error;

        // Format the data
        const formattedSnaps = data.map(snap => ({
          ...snap,
          categories: snap.categories?.map((c: any) => c.category) || [],
          commentCount: snap.comments?.[0]?.count || 0,
          isOwner: user?.id === snap.creator_id,
          userLiked: false, // Default value, we don't track this for non-logged in users
          isMarked: false,
          likes_count: snap.snap_likes?.[0]?.count || 0 // Use the live count from snap_likes
        }));

        setCashSnaps(formattedSnaps);
      } catch (error) {
        console.error('Error fetching CashSnaps:', error);
      } finally {
        setLoadingCash(false);
      }
    };

    fetchTopSnaps();
    fetchCashSnaps();
  }, [user]);

  const handleSnapClick = (snapId: string) => {
    if (user) {
      // If user is logged in, navigate directly to the SNAP
      navigate(`/snap/${snapId}`);
    } else {
      // If user is not logged in, store the SNAP ID in localStorage and redirect to login
      localStorage.setItem('redirectSnapId', snapId);
      navigate('/login');
    }
  };

  const handleDelete = () => {
    // This is just a placeholder - deletion won't be available on the landing page
    console.log("Delete operation not available on landing page");
  };

  const handleLike = (id: string, event: React.MouseEvent) => {
    // This is just a placeholder - liking won't be available on the landing page
    event.stopPropagation();
    console.log("Like operation not available on landing page");
  };

  // Calculate activity thresholds for proper display
  const thresholds = getActivityThresholds([...topSnaps, ...cashSnaps]);

  if (loading && loadingCash) {
    return (
      <div id="top-snaps-section" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Trending Now</h2>
            <h1 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Top SNAPs
            </h1>
          </div>
          <div className="mt-10 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (topSnaps.length === 0 && cashSnaps.length === 0) {
    return null;
  }

  return (
    <div id="top-snaps-section" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {topSnaps.length > 0 && (
          <>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Trending Now</h2>
              <h1 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Top SNAPs
              </h1>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                See what the community is voting on right now
              </p>
            </motion.div>

            <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {topSnaps.map((snap, index) => (
                <motion.div
                  key={snap.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="cursor-pointer h-full"
                >
                  <SnapCard
                    snap={{
                      ...snap,
                      activity: getSnapActivity(snap, thresholds)
                    }}
                    onDelete={handleDelete}
                    onLike={handleLike}
                  />
                </motion.div>
              ))}
            </div>
          </>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <Button
            onClick={() => user ? navigate('/dashboard') : navigate('/login')}
            variant="primary"
            className="px-8 py-3 md:py-4 md:px-10 text-base md:text-lg"
          >
            Show SNAPs
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}